// src/features/readingnote/pages/AllReviewsPage.tsx
import React, { useCallback, useEffect, useState } from "react";
import { usePageScale } from "@/ui/usePageScale";
import { PageShell } from "@/ui/PageShell";
import { HEADER, withBase } from "@/ui/Header";
import { reviewsAll, reviewToggleLike, isLoggedIn } from "@/lib/api";
import { notify } from "@/ui/notify";
import { useNavigate } from "react-router-dom";
import { registerPager, unregisterPager } from "@/lib/pagerBridge";
import { toCoverUrl } from "@/lib/coverUrl";
import { SPC_MODEL, getDeviceModel } from "@/lib/androidBridge";
import { COLOR, FW, BORDER, RADIUS } from "@/ui/tokens";

/* ───────────────── Config ───────────────── */
const BASE = {
    W: 1264,
    MARGIN_X: 137,
    TAB_H: 78,
    TAB_FZ: 35,
    GAP: 24,
    THUMB_W: 230,
    THUMB_H: 310,
    TITLE_FZ: 34,
    BODY_FZ: 28,
    ROW_VPAD: 18,
    ROW_GAP: 20,
    PAGER_H: 61,
    PAGER_TOP_GAP: 0,
} as const;

const ICONS = {
    header: withBase("icons/reading_note.svg"),
    first:  withBase("icons/prev2.svg"),
    prev:   withBase("icons/prev1.svg"),
    next:   withBase("icons/next1.svg"),
    last:   withBase("icons/next2.svg"),
};

/* ───────────────── StarIcon ───────────────── */
function StarIcon({ filled, size }: { filled: boolean; size: number }) {
    const c = filled ? "#111" : "#D1D5DB";
    return (
        <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true" style={{ display: "block" }}>
            <path
                d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z"
                fill={c} stroke={c} strokeWidth={2.6} strokeLinejoin="round"
            />
        </svg>
    );
}

/* ───────────────── Scale ───────────────── */
function useScale() {
    const { s, px, sf, sfpx } = usePageScale({ baseW: BASE.W, max: 1 });
    return { s, px, sf, sfpx };
}

/* ───────────────── Tabs ───────────────── */
type SortKey = "latest" | "popular";

function Tabs({ value, onChange, px, s, sfpx }: {
    value: SortKey; onChange: (v: SortKey) => void;
    px: (n: number) => string; s: (n: number) => number; sfpx: (n: number) => string;
}) {
    const Tab = ({ keyVal, label }: { keyVal: SortKey; label: string }) => (
        <button
            onClick={() => onChange(keyVal)}
            style={{
                height: px(BASE.TAB_H), width: "100%",
                fontSize: sfpx(BASE.TAB_FZ), fontWeight: FW.extraBold,
                background: "transparent", border: "none", cursor: "pointer", color: COLOR.text,
                borderBottom: value === keyVal ? `${s(4)}px solid #000` : `${s(2)}px solid #e5e7eb`,
            }}
        >
            {label}
        </button>
    );
    return (
        <div style={{ width: "100%", display: "grid", gridTemplateColumns: "1fr 1fr" }}>
            <Tab keyVal="latest"  label="최신순" />
            <Tab keyVal="popular" label="인기순" />
        </div>
    );
}

/* ───────────────── Pager ───────────────── */
function Pager({ page, totalPages, hasNext, onFirst, onPrev, onNext, onLast, px, sfpx }: {
    page: number; totalPages: number; hasNext: boolean;
    onFirst: () => void; onPrev: () => void; onNext: () => void; onLast: () => void;
    px: (n: number) => string; sfpx: (n: number) => string;
}) {
    return (
        <div style={{ width: "100%", height: "100%" }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%" }}>
                <button disabled={!(page > 1)} onClick={onFirst}
                    style={{ lineHeight: 0, background: "transparent", marginRight: px(48), border: "none", opacity: page > 1 ? 1 : 0.4, cursor: "pointer" }}>
                    <img src={ICONS.first} alt="" style={{ width: px(48), height: px(48), display: "block" }} />
                </button>
                <button disabled={!(page > 1)} onClick={onPrev}
                    style={{ lineHeight: 0, background: "transparent", marginRight: px(46), border: "none", opacity: page > 1 ? 1 : 0.4, cursor: "pointer" }}>
                    <img src={ICONS.prev} alt="" style={{ width: px(48), height: px(48), display: "block" }} />
                </button>
                <span style={{ fontWeight: 700, fontSize: sfpx(34), minWidth: px(120), textAlign: "center", marginRight: px(46) }}>
                    {Math.max(1, page)} / {Math.max(1, totalPages)}
                </span>
                <button disabled={!hasNext} onClick={onNext}
                    style={{ lineHeight: 0, background: "transparent", marginRight: px(48), border: "none", opacity: hasNext ? 1 : 0.4, cursor: "pointer" }}>
                    <img src={ICONS.next} alt="" style={{ width: px(48), height: px(48), display: "block" }} />
                </button>
                <button disabled={!hasNext} onClick={onLast}
                    style={{ lineHeight: 0, background: "transparent", border: "none", opacity: hasNext ? 1 : 0.4, cursor: "pointer" }}>
                    <img src={ICONS.last} alt="" style={{ width: px(48), height: px(48), display: "block" }} />
                </button>
            </div>
        </div>
    );
}

/* ───────────────── ReviewRow ───────────────── */
function ReviewRow({ it, px, s, sfpx, thumbW, thumbH, onCoverClick, onLike }: {
    it: any; px: (n: number) => string; s: (n: number) => number; sfpx: (n: number) => string;
    thumbW: number; thumbH: number;
    onCoverClick: (it: any) => void;
    onLike: (id: number) => void;
}) {
    const keywords: string[] = it.keywords || [];
    const rating = Math.max(0, Math.min(5, Number(it.rating || 0)));
    const starSize = Math.round(s(24));

    return (
        <div
            style={{
                display: "grid",
                gridTemplateColumns: `${thumbW}px 1fr auto`,
                gap: px(BASE.ROW_GAP),
                padding: `${px(BASE.ROW_VPAD)} 0`,
                borderBottom: BORDER.muted,
                height: `${thumbH + s(BASE.ROW_VPAD) * 2}px`,
                overflow: "hidden",
                boxSizing: "border-box",
            }}
        >
            {/* 표지 - 클릭 시 책 상세 이동 */}
            <div
                onClick={() => onCoverClick(it)}
                style={{
                    width: `${thumbW}px`, height: `${thumbH}px`,
                    display: "grid", placeItems: "center",
                    background: COLOR.bgLighter, border: BORDER.light,
                    borderRadius: RADIUS.md, overflow: "hidden", cursor: it.Book ? "pointer" : "default",
                }}
            >
                {it.Book?.cover_url ? (
                    <img
                        src={toCoverUrl(it.Book.cover_url)} alt=""
                        loading="lazy" referrerPolicy="no-referrer"
                        style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain", display: "block" }}
                    />
                ) : (
                    <span style={{ color: "#9ca3af", fontSize: 12 }}>No Image</span>
                )}
            </div>

            {/* 텍스트 영역 */}
            <div style={{ display: "flex", flexDirection: "column", minWidth: 0, height: "100%", overflow: "hidden" }}>
                {/* 제목 */}
                <div style={{ flexShrink: 0, fontWeight: FW.extraBold, marginBottom: 8, fontSize: sfpx(BASE.TITLE_FZ), lineHeight: 1.22, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {it.Book?.title || "제목 없음"}
                </div>

                {/* 본문 */}
                <div style={{ flex: 1, minHeight: 0, overflow: "hidden" }}>
                    <div style={{
                        color: "#444", fontSize: sfpx(BASE.BODY_FZ), lineHeight: 1.55,
                        display: "-webkit-box", WebkitBoxOrient: "vertical" as any,
                        WebkitLineClamp: 3 as any, overflow: "hidden",
                        textOverflow: "ellipsis", whiteSpace: "pre-wrap", wordBreak: "break-word",
                    }}>
                        {String(it.content || "")}
                    </div>
                </div>

                {/* 별 + 닉네임 + 키워드 */}
                <div style={{ flexShrink: 0, display: "flex", flexDirection: "column", gap: px(7), marginTop: px(8) }}>
                    {/* 별 + 닉네임 한 줄 */}
                    <div style={{ display: "flex", alignItems: "center", gap: px(10), flexWrap: "wrap" }}>
                        <span style={{ display: "inline-flex", alignItems: "center", gap: px(4) }}>
                            {[1, 2, 3, 4, 5].map(n => (
                                <StarIcon key={n} filled={n <= rating} size={starSize} />
                            ))}
                        </span>
                        <span style={{ color: "#666", fontSize: sfpx(22), fontWeight: 600 }}>
                            {it.author?.nickname || "익명"}
                        </span>
                    </div>

                    {/* 키워드 */}
                    {keywords.length > 0 && (
                        <div style={{ display: "flex", flexWrap: "wrap", gap: px(6), alignItems: "center" }}>
                            {keywords.map((k, i) => (
                                <span key={i} style={{
                                    display: "inline-block",
                                    padding: `${px(4)} ${px(10)}`,
                                    fontSize: sfpx(19), fontWeight: 700,
                                    borderRadius: RADIUS.full, border: BORDER.dark,
                                    background: COLOR.white, color: COLOR.black,
                                    whiteSpace: "nowrap", lineHeight: 1.3,
                                }}>{k}</span>
                            ))}
                        </div>
                    )}
                </div>
            </div>

            {/* 좋아요 버튼 */}
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "flex-end" }}>
                <button
                    onClick={() => !it.is_mine && onLike(it.id)}
                    style={{
                        display: "flex", flexDirection: "column", alignItems: "center", gap: px(4),
                        background: "transparent", border: "none",
                        cursor: it.is_mine ? "default" : "pointer",
                        opacity: it.is_mine ? 0.35 : 1,
                        padding: px(4),
                    }}
                >
                    <span style={{ fontSize: sfpx(36), lineHeight: 1, color: it.liked_by_me ? "#e53e3e" : "#bbb" }}>
                        {it.liked_by_me ? "♥" : "♡"}
                    </span>
                    <span style={{ fontSize: sfpx(22), fontWeight: 600, color: "#999" }}>
                        {it.likes_count || 0}
                    </span>
                </button>
            </div>
        </div>
    );
}

/* ───────────────── Page ───────────────── */
const PAGE_SIZE = 3;

export default function AllReviewsPage() {
    const { s, px, sfpx } = useScale();
    const nav = useNavigate();

    const [sort, setSort] = useState<SortKey>("latest");
    const [items, setItems] = useState<any[]>([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);

    const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
    const hasNext = page < totalPages;

    // 리뷰 목록 로드
    useEffect(() => {
        (async () => {
            try {
                const r = await reviewsAll({ page, size: PAGE_SIZE, sort });
                setItems(r.items || []);
                setTotal(r.total || 0);
            } catch {
                setItems([]);
                setTotal(0);
            }
        })();
    }, [page, sort]);

    // 탭 변경 시 1페이지로
    const handleSort = (v: SortKey) => { setSort(v); setPage(1); };

    // 물리키 페이저
    useEffect(() => {
        registerPager({
            next:  () => setPage(p => Math.min(totalPages, p + 1)),
            prev:  () => setPage(p => Math.max(1, p - 1)),
            first: () => setPage(1),
            last:  () => setPage(totalPages),
        });
        return () => unregisterPager();
    }, [totalPages]);

    // 좋아요 토글
    const handleLike = useCallback(async (id: number) => {
        if (!isLoggedIn()) {
            nav("/auth/login", { state: { from: "/review-feed" } });
            return;
        }
        try {
            const res = await reviewToggleLike(id);
            setItems(prev => prev.map(it =>
                it.id === id ? { ...it, liked_by_me: res.liked, likes_count: res.likes_count } : it
            ));
        } catch (e: any) {
            if (e?.code === "SELF_LIKE_FORBIDDEN") {
                notify("내 리뷰에는 좋아요를 누를 수 없습니다.");
            }
        }
    }, [nav]);

    // 표지 클릭 → 책 상세
    const handleCoverClick = useCallback((it: any) => {
        const b = it.Book;
        if (!b) return;
        const id = b.isbn13 || b.isbn10 || `work-${b.id}`;
        nav(`/books/${id}`, { state: { book: b } });
    }, [nav]);

    const isSpc = getDeviceModel() === SPC_MODEL;
    const { thumbW, thumbH, pagerH } = React.useMemo(() => {
        if (isSpc) {
            const cg = s(24);
            const cardW = Math.floor((s(1048) - cg * 2) / 3) - 5;
            const ratio = cardW / s(205);
            return { thumbW: cardW, thumbH: Math.round(s(295) * ratio), pagerH: s(140) };
        }
        return { thumbW: s(BASE.THUMB_W), thumbH: s(BASE.THUMB_H), pagerH: s(BASE.PAGER_H) };
    }, [s, isSpc]);

    const rowBlock = thumbH + s(BASE.ROW_VPAD) * 2;
    const listH = PAGE_SIZE * rowBlock + s(BASE.ROW_GAP) * (PAGE_SIZE - 1);

    return (
        <PageShell title="전체 리뷰" icon={ICONS.header} headerHeight={HEADER.H} width={px(BASE.W)}>
            <div style={{ gridArea:"body", display:"flex", flexDirection:"column", height:"100%" }}>
                {/* 탭 */}
                <Tabs value={sort} onChange={handleSort} px={px} s={s} sfpx={sfpx} />

                {/* 콘텐츠 — takes remaining space */}
                <div style={{ flex:1, minHeight:0, overflow:"hidden" }}>
                    <div style={{ width: px(BASE.W), margin:"0 auto", height:"100%" }}>
                        <div style={{ marginLeft: px(BASE.MARGIN_X), marginRight: px(BASE.MARGIN_X), paddingTop: px(BASE.GAP) }}>
                            <div style={{ height: `${listH}px`, overflow: "hidden", boxSizing: "border-box" }}>
                                {items.map((it: any) => (
                                    <ReviewRow
                                        key={it.id} it={it} px={px} s={s} sfpx={sfpx}
                                        thumbW={thumbW} thumbH={thumbH}
                                        onCoverClick={handleCoverClick}
                                        onLike={handleLike}
                                    />
                                ))}
                                {total === 0 && (
                                    <div style={{ padding: px(24), color: "#666", fontSize: sfpx(BASE.BODY_FZ) }}>
                                        작성된 리뷰가 없습니다.
                                    </div>
                                )}
                                {items.length < PAGE_SIZE && Array.from({ length: PAGE_SIZE - items.length }).map((_, i) => (
                                    <div key={`empty-${i}`} style={{ height: `${rowBlock}px`, borderBottom: i < PAGE_SIZE - items.length - 1 ? BORDER.muted : "none" }} />
                                ))}
                            </div>
                        </div>
                    </div>
                </div>

                {/* 페이저 — pinned to bottom */}
                <div style={{ flexShrink:0, height:`${pagerH}px`, marginLeft: px(BASE.MARGIN_X), marginRight: px(BASE.MARGIN_X) }}>
                    <Pager
                        page={page} totalPages={totalPages} hasNext={hasNext}
                        onFirst={() => setPage(1)}
                        onPrev={() => setPage(p => Math.max(1, p - 1))}
                        onNext={() => setPage(p => Math.min(totalPages, p + 1))}
                        onLast={() => setPage(totalPages)}
                        px={px}
                        sfpx={sfpx}
                    />
                </div>
            </div>
        </PageShell>
    );
}
